import React from 'react';
import { MetricCardBlock } from './blocks/MetricCardBlock';
import { LineChartBlock } from './blocks/LineChartBlock';
import { BarChartBlock } from './blocks/BarChartBlock';
import { TableBlock } from './blocks/TableBlock';
import { KpiHeroBlock } from './blocks/KpiHeroBlock';
import { TextBlock } from './blocks/TextBlock';
import { SeparatorBlock } from './blocks/SeparatorBlock';
import { BlockInsight } from './blocks/BlockInsight';
import { BlockActions } from './blocks/BlockActions';
import { ChannelLogo } from '../channels/ChannelLogo';

const LAYOUT_TYPES = new Set(['separator']);

const DEFAULT_COL_SPANS = {
    kpi_hero: 3,
    metric_card: 6,
    line_chart: 12,
    bar_chart: 6,
    table: 12,
    text: 12,
    separator: 12,
};

function getDefaultColSpan(type) {
    return DEFAULT_COL_SPANS[type] || 12;
}

function isContentBlock(block) {
    if (block.type === 'separator') return false;
    if (block.type === 'text' && !block.data?.content) return false;
    return true;
}

function filterOrphanBlocks(blocks) {
    return blocks.filter((block, i) => {
        // Skip empty text blocks
        if (block.type === 'text' && !block.data?.content) return false;
        // Skip separators not followed by content before next separator or end
        if (block.type === 'separator') {
            for (let j = i + 1; j < blocks.length; j++) {
                if (blocks[j].type === 'separator') return false;
                if (isContentBlock(blocks[j])) return true;
            }
            return false;
        }
        return true;
    });
}

export function ReportBlocks({ blocks, reportId, isAuthenticated }) {
    if (!blocks || blocks.length === 0) {
        return (
            <section className="report-blocks">
                <p className="no-blocks">No hay datos disponibles en este informe.</p>
            </section>
        );
    }

    const visibleBlocks = filterOrphanBlocks(blocks);

    const renderBlock = (block) => {
        const BlockComponent = getBlockComponent(block.type);
        const colSpan = block.col_span || getDefaultColSpan(block.type);
        const isLayout = LAYOUT_TYPES.has(block.type);

        if (isLayout) {
            return (
                <div
                    key={block.id}
                    className="report-block report-block--layout"
                    style={{ gridColumn: `span ${colSpan}` }}
                >
                    <BlockComponent data={block.data} />
                </div>
            );
        }

        const showBlockTitle = block.title && block.type !== 'kpi_hero';
        const blockChannels = block.query_params?.channels;
        const showChannelLogos = blockChannels && blockChannels.length > 0;

        return (
            <div
                key={block.id}
                className={`report-block report-block--${block.type}`}
                style={{ gridColumn: `span ${colSpan}` }}
            >
                {showBlockTitle && (
                    <div className="block-header">
                        <h3 className="block-title">{block.title}</h3>
                        {showChannelLogos && (
                            <div className="block-header-channels">
                                {blockChannels.map(ch => (
                                    <ChannelLogo key={ch} channel={ch} height={14} width={14} hspace={0} vspace={0}/>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                <div className="block-content">
                    <BlockComponent data={block.data} />
                </div>
                {block.ai_insight && (
                    <BlockInsight insight={block.ai_insight} />
                )}
                {isAuthenticated && <BlockActions reportId={reportId} block={block} />}
            </div>
        );
    };

    return (
        <section className="report-blocks">
            <div className="blocks-grid">
                {visibleBlocks.map(renderBlock)}
            </div>
        </section>
    );
}

function getBlockComponent(type) {
    switch (type) {
        case 'kpi_hero':
            return KpiHeroBlock;
        case 'metric_card':
            return MetricCardBlock;
        case 'line_chart':
            return LineChartBlock;
        case 'bar_chart':
            return BarChartBlock;
        case 'table':
            return TableBlock;
        case 'text':
            return TextBlock;
        case 'separator':
            return SeparatorBlock;
        default:
            return TableBlock;
    }
}
